/**
 * クイズ「へ」。そっくりな2つの字のどちらが出ているかを当てる。
 *
 * 組（へ／ヘ、ロ／口、エ／工、タ／夕）と書体は
 * `scripts/fetch_he_fonts.mjs` が作った `quiz-pairs.ts` から読む。
 * 書体は2つの形が違うものだけに絞ってあるので、見れば必ず分かる。
 */
import { QUIZ_PAIRS, type QuizPair } from './quiz-pairs.js';
import { openXPostOnWeb } from './x-post.js';
import './pages.css';

const QUESTIONS = 10;

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;

interface Q {
  family: string;
  /** b の字が出ているか（false なら a） */
  isB: boolean;
}

/** 並びをまぜる（Fisher–Yates） */
function shuffle<T>(a: T[]): T[] {
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j] as T, a[i] as T];
  }
  return a;
}

/** 10問つくる。書体はかぶらせず、2つの字が半々になるようにする */
function makeQuiz(pair: QuizPair): Q[] {
  const fonts = shuffle([...pair.fonts]).slice(0, QUESTIONS);
  const kinds = shuffle([...Array(fonts.length)].map((_, i) => i < fonts.length / 2));
  return fonts.map((family, i) => ({ family, isB: kinds[i] as boolean }));
}

let pair = QUIZ_PAIRS[0] as QuizPair;
let quiz = makeQuiz(pair);
let at = 0;
let score = 0;
/** 各問の結果（undefined はまだ答えていない） */
let marks: (boolean | undefined)[] = [];

const glyph = $('qGlyph');
const result = $('qResult');

/** クイズに出る字ぜんぶ */
const GLYPHS = QUIZ_PAIRS.flatMap((p) => [p.a.ch, p.b.ch]).join('');

/**
 * その書体が使えるようになるまで待つ。
 * **読めなくても 2.5 秒であきらめて出す。** ここで止まると、字が出ないまま固まってしまう。
 */
function waitFont(family: string): Promise<unknown> {
  return Promise.race([
    document.fonts.load(`40px "${family}"`, GLYPHS).catch(() => undefined),
    new Promise((done) => setTimeout(done, 2500)),
  ]);
}

/** 何問目を出しているか。待っているあいだに次へ進んだら、古い結果は捨てる */
let seq = 0;

function renderDots(): void {
  $('qDots').innerHTML = quiz
    .map((_, i) => {
      const m = marks[i];
      const cls = m === undefined ? (i === at ? 'now' : '') : m ? 'ok' : 'ng';
      return `<li class="${cls}"></li>`;
    })
    .join('');
}

function show(): void {
  const q = quiz[at] as Q;
  renderDots();
  // 書体が届く前に出すと、別の書体で描かれてクイズにならない
  const me = ++seq;
  const have = document.fonts.check(`40px "${q.family}"`, GLYPHS);
  if (!have) document.body.classList.remove('fonts-ready');
  void waitFont(q.family).then(() => {
    if (me === seq) document.body.classList.add('fonts-ready');
  });
  $('qScore').textContent = `${score}問正解`;
  $('qFont').innerHTML = '&nbsp;';
  glyph.style.fontFamily = `"${q.family}"`;
  glyph.textContent = q.isB ? pair.b.ch : pair.a.ch;
  glyph.classList.remove('done');
  result.hidden = true;
  $('qA').removeAttribute('disabled');
  $('qB').removeAttribute('disabled');
}

function answer(saidB: boolean): void {
  const q = quiz[at] as Q;
  const right = saidB === q.isB;
  marks[at] = right;
  if (right) score++;
  renderDots();
  $('qScore').textContent = `${score}問正解`;
  $('qFont').textContent = `書体：${q.family}`;
  glyph.classList.add('done');
  $('qA').setAttribute('disabled', '');
  $('qB').setAttribute('disabled', '');
  const side = q.isB ? pair.b : pair.a;
  const judge = $('qJudge');
  judge.textContent = right
    ? `正解！ ${side.label}の「${side.ch}」`
    : `ざんねん。${side.label}の「${side.ch}」でした`;
  judge.classList.toggle('ng', !right);
  for (const [id, one] of [
    ['cmpA', pair.a],
    ['cmpB', pair.b],
  ] as const) {
    const el = $(id);
    el.style.fontFamily = `"${q.family}"`;
    el.textContent = one.ch;
    $(`${id}Label`).textContent = `${one.label} ${one.code}`;
  }
  $('qHint').textContent = `この書体で並べるとこう。${pair.note}です。`;
  $('qNext').textContent = at + 1 >= quiz.length ? '結果を見る' : '次の問題';
  result.hidden = false;
}

function next(): void {
  at++;
  if (at < quiz.length) {
    show();
    return;
  }
  $('quiz').hidden = true;
  const done = $('qDone');
  done.hidden = false;
  $('doneWhat').textContent = `クイズ「${pair.title}」の結果`;
  $('doneScore').textContent = String(score);
  // 出た10字を横一列で見返せるようにする
  $('review').innerHTML = quiz
    .map((q, i) => {
      const one = q.isB ? pair.b : pair.a;
      const ok = marks[i] === true;
      return (
        `<li class="${ok ? 'ok' : 'ng'}">` +
        `<b style="font-family:&quot;${q.family}&quot;">${one.ch}</b>` +
        `<i>${ok ? '○' : '×'}</i><small>${one.label}</small></li>`
      );
    })
    .join('');
  const [title, msg] =
    score === quiz.length
      ? ['もじマスター', '完璧です。字の形をよく見ていますね。']
      : score >= 8
        ? ['もじ名人', 'かなりの目利き。細かい違いが見えています。']
        : score >= 5
          ? ['ふつうの人間', 'まぎらわしいですよね。半分当てられたら上出来です。']
          : ['勘で生きる人', 'ほとんど同じ形なので、しかたありません。'];
  $('doneTitle').textContent = title;
  $('doneMsg').textContent = msg;
  shareText =
    `クイズ「${pair.title}」で ${score}/${quiz.length}問正解（${title}）\n` +
    `${pair.note}、見分けられる？\nhttps://mojitower.abarabob.com/he`;
  $<HTMLAnchorElement>('qShare').href = `https://x.com/intent/post?text=${encodeURIComponent(shareText)}`;
}

/** X に投稿する文章 */
let shareText = '';

// スマホでは X のアプリで開く（ブラウザだとログインを求められる）
$('qShare').addEventListener('click', (e) => {
  const url = $<HTMLAnchorElement>('qShare').href;
  if (openXPostOnWeb(shareText, url)) e.preventDefault();
});

/** 組を選び直して、最初から始める */
function start(next: QuizPair, scroll = false): void {
  pair = next;
  quiz = makeQuiz(pair);
  at = 0;
  score = 0;
  marks = [];
  $('qA').textContent = pair.a.label;
  $('qB').textContent = pair.b.label;
  $('qDone').hidden = true;
  $('quiz').hidden = false;
  $('lead').innerHTML =
    `${pair.note}。コンピューターの中では別の字ですが、形がそっくりで見分けがつきません。` +
    '出てきた字がどちらか、10問あてられますか？';
  $('h1').textContent = `クイズ「${pair.title}」`;
  document.title = `クイズ「${pair.title}」｜もじタワー`;
  for (const b of document.querySelectorAll<HTMLButtonElement>('#pairPick button')) {
    b.setAttribute('aria-pressed', String(b.dataset['id'] === pair.id));
  }
  show();
  if (scroll) $('quiz').scrollIntoView({ behavior: 'smooth', block: 'start' });
}

$('pairPick').innerHTML = QUIZ_PAIRS.map(
  (p) =>
    `<button type="button" data-id="${p.id}" aria-pressed="${p.id === pair.id}">` +
    `<b>${p.a.ch}／${p.b.ch}</b><span>${p.note}</span></button>`,
).join('');
$('pairPick').addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-id]');
  const found = b && QUIZ_PAIRS.find((p) => p.id === b.dataset['id']);
  if (found) start(found, true);
});

$('qA').addEventListener('click', () => answer(false));
$('qB').addEventListener('click', () => answer(true));
$('qNext').addEventListener('click', next);
$('qAgain').addEventListener('click', () => start(pair));

// 先に読んでおくと、問題が切り替わるたびに待たずに済む。
// ぜんぶで 70KB ほど。1つでも失敗したら止まる、ということがないようにする
const families = [...new Set(QUIZ_PAIRS.flatMap((p) => p.fonts))];
void Promise.allSettled(families.map((f) => document.fonts.load(`40px "${f}"`, GLYPHS)));

// 「まったく同じ形だった書体」のひとこと
const same = QUIZ_PAIRS.find((p) => p.same.length);
if (same) {
  $('sameNote').textContent =
    `ちなみに ${same.same.join('、')} の${same.same.length}書体は、「${same.a.ch}」と「${same.b.ch}」が` +
    'まったく同じ形でした。これではどうやっても見分けられないので、クイズには出していません。';
}

start(pair);
